<template>
  <div class="to-cc-inputs">
    <div class="input-section to-input">
      <span class="label">TO</span>
      <div>
        <el-select
          ref="toInputSelect"
          v-model="internalToEmailAsArray"
          :class="{'hide-input' : internalToEmailAsArray.length > 0}"
          :filter-method="debounceHandleToInputValue"
          :teleported="false"
          :popper-options="popperOptions"
          :loading="isLoadingCustomers"
          :popper-class="popperClass"
          loading-text="..."
          filterable
          allow-create
          placeholder=""
          reserve-keyword
          multiple
          @keyup.tab="handleToDelimiter"
          @keyup.enter="handleToDelimiter"
          @visible-change="handleToVisibleChange"
          @change="clearInputFields"
        >
          <el-option
            v-for="customer in filteredAvailableCustomers"
            :key="customer.id"
            :value="customer.email"
            :label="getToLabel(customer)"
          >
            <span>
              <avatar :person="customer" :size="18" />
              <span v-html="getOptionsHtmlLabel(customer)" />
            </span>
          </el-option>
        </el-select>
      </div>
    </div>
    <div class="input-section cc-input">
      <span class="label">CC</span>
      <div>
        <el-select
          ref="ccInputSelect"
          v-model="internalCcdEmails"
          :filter-method="debounceHandleCcInputValue"
          :teleported="false"
          :popper-options="popperOptions"
          :loading="isLoadingCustomers"
          :popper-class="popperClass"
          loading-text="..."
          filterable
          allow-create
          placeholder=""
          reserve-keyword
          multiple
          @keyup.tab="handleCcDelimiter"
          @keyup.enter="handleCcDelimiter"
          @visible-change="handleCcVisibleChange"
          @change="clearInputFields"
        >
          <el-option
            v-for="customer in filteredAvailableCustomers"
            :key="customer.id"
            :value="customer.email"
          >
            <span>
              <avatar :person="customer" :size="18" />
              <span v-html="getOptionsHtmlLabel(customer)" />
            </span>
          </el-option>
        </el-select>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'
import { debounce } from 'lodash-es'
import { ClickOutside as Clickoutside } from 'element-plus'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'
import PopperAssistant from '@/helpers/popper-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

/* The parts of el-select's instance that this component works with */
interface ISelectInternals {
  states: { inputValue: string }
  inputRef: HTMLInputElement
  selectionRef: HTMLElement
  focus(): void
}

/* 
  This component is used for entering email addresses for conversations. It will load all existing
  customers from the passed-in mailbox ID so a user can select from the list (or type in their own)
*/
export default defineComponent({
  name: 'ToCcInputs',

  directives: { Clickoutside },

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    /* For sole purpose of pre-stubbing "to" label with the name since the list of customers
       for the mailbox is not pulled unless the user types into the "to" or "cc" field.
       This will add the customer to the availableCustomers array on creation to allow el-select
       to display the name on the "to" label. If a user types into the "to" or "cc" fields,
       the availableCustomers array is re-hydrated from the DB. */
    initialToCustomer: {
      type: Object as () => IPersonWithEmail,
      default: null,
    },
    toEmail: {
      type: String,
      required: true,
    },
    ccdEmails: {
      type: Array as () => Array<string>,
      required: true,
    },
    focusTo: {
      type: Boolean,
      default: false,
    },
  },

  emits: [
    'update-ccd-emails',
    'update-to-email',
    'update:ccdEmails',
    'update:toEmail',
  ],

  data() {
    return {
      // Fixed inside dialogs and drawers, so their scrolling body doesn't cut the dropdowns off
      popperOptions: {},
      isLoadingCustomers: false,
      shouldRefreshAvailableCustomers: true,

      availableCustomers: [] as IPersonWithEmail[],
      filteredAvailableCustomers: [] as IPersonWithEmail[],

      curToInputValue: '',
      curCcInputValue: '',

      debounceHandleToInputValue: null as any,
      debounceHandleCcInputValue: null as any,
    }
  },

  computed: {
    internalToEmail: {
      get(): string {
        return this.toEmail
      },
      set(val: string) {
        if (!TextAssistant.isValidEmail(val)) {
          val = ''
          // @ts-ignore
          this.toSelect().focus()
        } else {
          // @ts-ignore
          this.ccSelect().focus()
        }
        /* Can use sync or custom 'update-to-email' event to handle toEmail update */
        this.$emit('update:toEmail', val)
        this.$emit('update-to-email', val)
      },
    },
    internalToEmailAsArray: {
      get(): Array<string> {
        return this.toEmail.length > 0 ? [this.toEmail] : []
      },
      set(val: Array<string>) {
        this.internalToEmail = val.length > 0 ? val[0] : ''
      },
    },
    internalCcdEmails: {
      get(): Array<string> {
        return this.ccdEmails
      },
      set(val: Array<string>) {
        const cleansedVal = val.filter(v => TextAssistant.isValidEmail(v))
        /* Can use sync or custom 'update-ccd-emails' event to handle toEmail update */
        this.$emit('update:ccdEmails', cleansedVal)
        this.$emit('update-ccd-emails', cleansedVal)
      },
    },
    popperClass(): string {
      let result = 'options-dropdown '

      if (this.filteredAvailableCustomers.length === 0) {
        result += 'remove'
      }
      return result
    },
  },

  watch: {
    focusTo: {
      immediate: true,
      handler(val) {
        if (val) {
          // @ts-ignore
          this.toSelect().focus()
        }
      },
    },
    initialToCustomer: {
      immediate: true,
      handler(val) {
        if (val && val.id !== 0) {
          /* This is done so the to input's getToLabel() is set with the initial customer's name and email, otherwise
             you will only see the default in the selected TO: the value of the <el-option> which is just the email from the
             internalToEmailAsArray value */
          this.availableCustomers = [val]
          this.filteredAvailableCustomers = [val]
          /* Since cur customer has changed, need to restub "To" and refresh upon typing...*/
          this.shouldRefreshAvailableCustomers = true
        }
      },
    },
  },

  created() {
    this.debounceHandleToInputValue = debounce(this.handleToInputValue, 250)
    this.debounceHandleCcInputValue = debounce(this.handleCcInputValue, 250)
  },

  mounted() {
    this.popperOptions = PopperAssistant.inPlacePopperOptions(this.$el)
    this.addInputPlaceholders()

    this.toSelect().selectionRef.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
    this.ccSelect().inputRef.addEventListener(
      'keyup',
      (event: KeyboardEvent) => {
        this.adjustCcDropdownPosition()
      },
    )
    this.ccSelect().selectionRef.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    toSelect(): ISelectInternals {
      return this.$refs.toInputSelect as unknown as ISelectInternals
    },

    ccSelect(): ISelectInternals {
      return this.$refs.ccInputSelect as unknown as ISelectInternals
    },

    getToLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return customer.name + ' <' + customer.email + '>'
      }
      return customer.email
    },
    getOptionsHtmlLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return (
          '<strong>' +
          customer.name +
          '</strong>&nbsp;&lt;' +
          customer.email +
          '&gt;'
        )
      }
      return customer.email
    },
    addInputPlaceholders() {
      const toInput: HTMLInputElement = this.$el.querySelector(
        '.to-input .el-select__input',
      )

      toInput.placeholder = 'Enter recipient address...'

      const ccInput: HTMLInputElement = this.$el.querySelector(
        '.cc-input .el-select__input',
      )

      ccInput.placeholder = 'Enter ccs...'
    },

    async filterAvailableCustomers(val: string) {
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      if (val.trim().length <= 1) {
        this.filteredAvailableCustomers = []
        return
      }
      // Skip load/prune if we are still in the customer API call...
      if (!this.isLoadingCustomers) {
        if (this.shouldRefreshAvailableCustomers) {
          try {
            this.isLoadingCustomers = true
            this.availableCustomers = await MailboxesAPI.get_Customers(
              this.mailboxId,
            )
            this.isLoadingCustomers = false
            this.shouldRefreshAvailableCustomers = false
          } catch (error) {
            this.$error.emit(error)
          }
        }

        const loweredVal = val.toLowerCase()

        this.filteredAvailableCustomers = this.availableCustomers
          .filter(
            i =>
              i.email.includes(loweredVal) ||
              i.name.toLowerCase().includes(loweredVal),
          )
          .slice(0, 200)
      }
    },

    attemptToEmailPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        this.internalToEmail = val
      }
      this.clearInputFields()
    },

    attemptCcEmailsPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        const newCcdEmails = [
          ...this.internalCcdEmails,
          val.toLowerCase().trim(),
        ]

        this.internalCcdEmails = Array.from(new Set(newCcdEmails))
      }
      this.clearInputFields()
    },

    clearInputFields() {
      this.curToInputValue = ''
      this.curCcInputValue = ''
      this.toSelect().states.inputValue = ''
      this.ccSelect().states.inputValue = ''
      this.filterAvailableCustomers('')
    },

    async handleToInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the select's query is empty whenever we
      lose focus*/
      this.curToInputValue = val
      if (val.trim().length < 1) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptToEmailPush(inputNoComma)
        }
      }
    },

    async handleCcInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the select's query is empty whenever we
      lose focus*/
      this.curCcInputValue = val

      this.adjustCcDropdownPosition()

      if (val.trim().length < 2) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptCcEmailsPush(inputNoComma)
        }
      }
    },

    handleToDelimiter() {
      const inputValue = this.toSelect().states.inputValue.trim()
      this.attemptToEmailPush(inputValue)
    },

    handleCcDelimiter() {
      const inputValue = this.ccSelect().states.inputValue.trim()
      this.attemptCcEmailsPush(inputValue)
    },

    handleToVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value) {
        this.attemptToEmailPush(this.curToInputValue)
      }
    },

    handleCcVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (value) {
        /* Clear out list on focus so we don't automatically see the 
        pre-stubbed initialToCustomer in filteredAvailableCustomers in the suggest list (which
        is needed to get the getToLabel() value right on the initial load...so the user
        sees the name of the initial to customer). Otherwise,
        it shows it automatically on focus for some reason. */
        this.filteredAvailableCustomers = []
      } else {
        this.attemptCcEmailsPush(this.curCcInputValue)
      }
    },

    adjustCcDropdownPosition() {
      const input: HTMLInputElement = this.$el.querySelector(
        '.cc-input .el-select__input',
      )
      const dropdown: HTMLElement = this.$el.querySelector(
        '.cc-input .el-select__popper',
      )
      if (dropdown) {
        dropdown.style.marginLeft = input.offsetLeft + 'px'
      }
    },
  },
})
</script>

<style lang="scss">
.to-cc-inputs {
  display: flex;
  flex-direction: column;
  margin-bottom: 20px;
  border-radius: 8px;
  border: solid 1px $color--gray-30;
  background-color: var(--color-background-header);

  > .input-section {
    display: flex;
    align-items: center;
    color: var(--color-text-secondary);
    padding: 0 10px;

    &.to-input {
      border-bottom: solid 1px $color--gray-30;
    }

    > .label {
      display: inline-block;
      @include lettering--h5;
      width: 16px;
    }

    > div {
      display: flex;
      flex: 1 1 auto;
      border-top: 1px solid transparent;
      border-right: 1px solid transparent;
      border-bottom: 1px solid transparent;

      @include media-breakpoint-down(sm) {
        overflow: hidden;
        overflow-x: auto;
      }
    }

    .hide-input {
      .el-select__input,
      .el-select__popper {
        display: none;
      }
    }

    .el-select {
      flex: 1 1 auto;

      &__input {
        margin-left: 10px;
        min-width: 200px;
        @include lettering--p-sm;

        @include media-breakpoint-down(sm) {
          width: 100%;
        }
      }

      /* Element Plus hides an empty filter input until the select is focused, but ours holds the placeholder */
      &__input-wrapper.is-hidden {
        position: static;
        opacity: 1;
      }

      &__selection {
        max-width: 100% !important;

        /* Element Plus pulls the tags 8px into padding this select doesn't have; Element UI gave them a 6px margin */
        &.is-near {
          margin-left: 6px;
        }
      }

      /* Only the tags and the filter input show: the select's own box is invisible */
      .el-select__wrapper,
      .el-select__wrapper.is-focused,
      .el-select__wrapper:hover {
        padding: 0;
        font-size: 15px;
        background-color: transparent;
        box-shadow: none;
        transition: none;
      }

      // Don't show caret
      .el-select__suffix {
        display: none;
      }

      .el-select__selection .el-tag {
        display: flex;
        align-items: center;
        padding: 1px 12px 0 12px;
        background-image: none;
        border: 0 !important;
        border-radius: 14px !important;

        &:hover {
          background-color: $color--gray-30;
        }
      }

      /* The dropdown class is on the select's popper */
      .el-popper.options-dropdown {
        min-width: 300px !important;
        max-width: 328px !important;

        &.absolute {
          position: absolute !important;
          top: 40px !important;
        }

        @include media-breakpoint-up(md) {
          max-width: 400px !important;
        }
      }

      &-dropdown__item {
        display: flex;
        align-items: center;
      }
    }

    .button {
      font-size: 16px;
      color: var(--color-text-third);
      text-decoration: none;
    }

    .ddi {
      top: 0;
      font-size: 0;
      line-height: 0;
      color: var(--color-text-third);

      &::before {
        width: 0;
        height: 0;
        font-size: 16px !important;
        line-height: 16px !important;
      }
    }
  }

  .options-dropdown {
    min-width: 100%;
    max-width: 100%;
    margin-left: 0;

    &.remove {
      display: none;
    }

    @include media-breakpoint-down(sm) {
      visibility: hidden;
    }
  }
}
</style>
